'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { HoverCard as HoverCardPrimitive } from 'radix-ui';
import { AnimatePresence, motion, useMotionValue, useSpring, type MotionValue, type HTMLMotionProps, type SpringOptions } from 'motion/react';
import { getStrictContext } from '@/lib/get-strict-context';
import { useControlledState } from '@/hooks/use-controlled-state';
type HoverCardContextType = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  x: MotionValue<number>;
  y: MotionValue<number>;
  followCursor?: boolean | 'x' | 'y';
  followCursorSpringOptions?: SpringOptions;
};
const [HoverCardProvider, useHoverCard] = getStrictContext<HoverCardContextType>('HoverCardContext');
type HoverCardProps = React.ComponentProps<typeof HoverCardPrimitive.Root> & {
  followCursor?: boolean | 'x' | 'y';
  followCursorSpringOptions?: SpringOptions;
};
function HoverCard(t0) {
  const $ = _c(23);
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      followCursor: t1,
      followCursorSpringOptions: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const followCursor = t1 === undefined ? false : t1;
  let t3;
  if ($[4] !== t2) {
    t3 = t2 === undefined ? {
      stiffness: 200,
      damping: 17
    } : t2;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const followCursorSpringOptions = t3;
  const t4 = props?.open;
  const t5 = props?.defaultOpen;
  const t6 = props?.onOpenChange;
  let t7;
  if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = {
      value: t4,
      defaultValue: t5,
      onChange: t6
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const [isOpen, setIsOpen] = useControlledState(t7);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  let t8;
  if ($[10] !== followCursor || $[11] !== followCursorSpringOptions || $[12] !== isOpen || $[13] !== setIsOpen || $[14] !== x || $[15] !== y) {
    t8 = {
      isOpen,
      setIsOpen,
      x,
      y,
      followCursor,
      followCursorSpringOptions
    };
    $[10] = followCursor;
    $[11] = followCursorSpringOptions;
    $[12] = isOpen;
    $[13] = setIsOpen;
    $[14] = x;
    $[15] = y;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== props || $[18] !== setIsOpen) {
    t9 = <HoverCardPrimitive.Root data-slot="hover-card" {...props} onOpenChange={setIsOpen} />;
    $[17] = props;
    $[18] = setIsOpen;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <HoverCardProvider value={t8}>{t9}</HoverCardProvider>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
type HoverCardTriggerProps = React.ComponentProps<typeof HoverCardPrimitive.Trigger>;
function HoverCardTrigger(t0) {
  const $ = _c(11);
  let onMouseMove;
  let props;
  if ($[0] !== t0) {
    ({
      onMouseMove,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onMouseMove;
    $[2] = props;
  } else {
    onMouseMove = $[1];
    props = $[2];
  }
  const {
    x,
    y,
    followCursor
  } = useHoverCard();
  let t1;
  if ($[3] !== followCursor || $[4] !== onMouseMove || $[5] !== x || $[6] !== y) {
    t1 = event => {
      onMouseMove?.(event);
      const target = event.currentTarget.getBoundingClientRect();
      if (followCursor === "x" || followCursor === true) {
        const eventOffsetX = event.clientX - target.left;
        const offsetXFromCenter = (eventOffsetX - target.width / 2) / 2;
        x.set(offsetXFromCenter);
      }
      if (followCursor === "y" || followCursor === true) {
        const eventOffsetY = event.clientY - target.top;
        const offsetYFromCenter = (eventOffsetY - target.height / 2) / 2;
        y.set(offsetYFromCenter);
      }
    };
    $[3] = followCursor;
    $[4] = onMouseMove;
    $[5] = x;
    $[6] = y;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleMouseMove = t1;
  let t2;
  if ($[8] !== handleMouseMove || $[9] !== props) {
    t2 = <HoverCardPrimitive.Trigger data-slot="hover-card-trigger" onMouseMove={handleMouseMove} {...props} />;
    $[8] = handleMouseMove;
    $[9] = props;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type HoverCardPortalProps = Omit<React.ComponentProps<typeof HoverCardPrimitive.Portal>, 'forceMount'>;
function HoverCardPortal(props) {
  const $ = _c(5);
  const {
    isOpen
  } = useHoverCard();
  let t0;
  if ($[0] !== isOpen || $[1] !== props) {
    t0 = isOpen && <HoverCardPrimitive.Portal forceMount={true} data-slot="hover-card-portal" {...props} />;
    $[0] = isOpen;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <AnimatePresence>{t0}</AnimatePresence>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
type HoverCardContentProps = React.ComponentProps<typeof HoverCardPrimitive.Content> & HTMLMotionProps<'div'>;
function HoverCardContent(t0) {
  const $ = _c(39);
  let align;
  let alignOffset;
  let arrowPadding;
  let avoidCollisions;
  let collisionBoundary;
  let collisionPadding;
  let hideWhenDetached;
  let props;
  let side;
  let sideOffset;
  let sticky;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      align,
      alignOffset,
      side,
      sideOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      style,
      transition: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = align;
    $[2] = alignOffset;
    $[3] = arrowPadding;
    $[4] = avoidCollisions;
    $[5] = collisionBoundary;
    $[6] = collisionPadding;
    $[7] = hideWhenDetached;
    $[8] = props;
    $[9] = side;
    $[10] = sideOffset;
    $[11] = sticky;
    $[12] = style;
    $[13] = t1;
  } else {
    align = $[1];
    alignOffset = $[2];
    arrowPadding = $[3];
    avoidCollisions = $[4];
    collisionBoundary = $[5];
    collisionPadding = $[6];
    hideWhenDetached = $[7];
    props = $[8];
    side = $[9];
    sideOffset = $[10];
    sticky = $[11];
    style = $[12];
    t1 = $[13];
  }
  let t2;
  if ($[14] !== t1) {
    t2 = t1 === undefined ? {
      type: "spring",
      stiffness: 300,
      damping: 25
    } : t1;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const transition = t2;
  const {
    x,
    y,
    followCursor,
    followCursorSpringOptions
  } = useHoverCard();
  const translateX = useSpring(x, followCursorSpringOptions);
  const translateY = useSpring(y, followCursorSpringOptions);
  let t3;
  let t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      scale: 0.5
    };
    t4 = {
      opacity: 1,
      scale: 1
    };
    t5 = {
      opacity: 0,
      scale: 0.5
    };
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
  }
  const t6 = followCursor === "x" || followCursor === true ? translateX : undefined;
  const t7 = followCursor === "y" || followCursor === true ? translateY : undefined;
  let t8;
  if ($[19] !== style || $[20] !== t6 || $[21] !== t7) {
    t8 = {
      x: t6,
      y: t7,
      ...style
    };
    $[19] = style;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== props || $[24] !== t8 || $[25] !== transition) {
    t9 = <motion.div key="hover-card-content" data-slot="hover-card-content" initial={t3} animate={t4} exit={t5} transition={transition} style={t8} {...props} />;
    $[23] = props;
    $[24] = t8;
    $[25] = transition;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== align || $[28] !== alignOffset || $[29] !== arrowPadding || $[30] !== avoidCollisions || $[31] !== collisionBoundary || $[32] !== collisionPadding || $[33] !== hideWhenDetached || $[34] !== side || $[35] !== sideOffset || $[36] !== sticky || $[37] !== t9) {
    t10 = <HoverCardPrimitive.Content forceMount={true} align={align} alignOffset={alignOffset} side={side} sideOffset={sideOffset} avoidCollisions={avoidCollisions} collisionBoundary={collisionBoundary} collisionPadding={collisionPadding} arrowPadding={arrowPadding} sticky={sticky} hideWhenDetached={hideWhenDetached} asChild={true}>{t9}</HoverCardPrimitive.Content>;
    $[27] = align;
    $[28] = alignOffset;
    $[29] = arrowPadding;
    $[30] = avoidCollisions;
    $[31] = collisionBoundary;
    $[32] = collisionPadding;
    $[33] = hideWhenDetached;
    $[34] = side;
    $[35] = sideOffset;
    $[36] = sticky;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}
type HoverCardArrowProps = React.ComponentProps<typeof HoverCardPrimitive.Arrow>;
function HoverCardArrow(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HoverCardPrimitive.Arrow data-slot="hover-card-arrow" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { HoverCard, HoverCardTrigger, HoverCardPortal, HoverCardContent, HoverCardArrow, useHoverCard, type HoverCardProps, type HoverCardTriggerProps, type HoverCardPortalProps, type HoverCardContentProps, type HoverCardArrowProps, type HoverCardContextType };